import {
  FREE_PRICE_LABEL,
  formatPrice,
  formatResetMoment,
  intervalAdverb,
  TIER_TAGLINES,
} from "@/components/billing/planCopy.ts";
import { OkThxWordmark } from "@/components/icons/Logo.tsx";
import { Badge } from "@/components/ui/badge.tsx";
import { Button } from "@/components/ui/button.tsx";
import { ButtonGroup } from "@/components/ui/button-group.tsx";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible.tsx";
import { Skeleton } from "@/components/ui/skeleton.tsx";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs.tsx";
import { api } from "@/lib/http.ts";
import { cn } from "@/lib/utils.ts";
import type { CatalogEntryResp, PriceResp, PublicCatalogResp } from "@/types/billing.ts";
import {
  BILLING_INTERVALS,
  PLAN_TIER_LABELS,
  type BillingInterval,
  type PlanTier,
} from "@/types/entitlements.ts";
import { queryOptions, useQuery } from "@tanstack/react-query";
import { Check, ChevronDown, Minus } from "lucide-react";
import { useState } from "react";
import { Link } from "wouter";

/**
 * Where every CTA lands. A signed-out visitor sees sign-in there and comes
 * back to it afterwards, since sign-in carries the path through as
 * `returnTo`; a signed-in one goes straight to their plan.
 */
const BILLING_PATH = "/settings/billing";

const pricingCatalogQuery = queryOptions({
  queryKey: ["pricing", "catalog"],
  queryFn: async ({ signal }) => {
    const res = await api.get<PublicCatalogResp>("/api/p/billing/catalog", { signal });
    return res.data;
  },
  // Prices change by redeploying Stripe config, not while someone reads.
  staleTime: 10 * 60_000,
});

/** What each card adds over the one before it. Plans differ in usage only. */
const CARD_BULLETS: Record<PlanTier, { lead: string | null; bullets: string[] }> = {
  free: {
    lead: null,
    bullets: [
      "Chat with okthx on the web and on iPhone",
      "Connect Gmail, Google Calendar and Outlook",
      "Email triage, drafts and replies",
      "Scheduling, invitations and reminders",
      "Routines, skills and memory",
      "A small allowance to try it out",
    ],
  },
  pro: {
    lead: "Everything in Free, plus:",
    bullets: [
      "Pro usage, the baseline every plan is sized against",
      "Enough for everyday hand-offs",
      "Upgrade or cancel any time",
    ],
  },
  power: {
    lead: "Everything in Pro, plus:",
    bullets: [
      "5x Pro's usage",
      "For hand-offs all through the day",
      "Usage so far carries into the bigger allowance when you upgrade",
    ],
  },
  max: {
    lead: "Everything in Power, plus:",
    bullets: [
      "20x Pro's usage, effectively unlimited",
      "For running your inbox, calendar and errands through okthx",
    ],
  },
};

const CTA_LABELS: Record<PlanTier, string> = {
  free: "Get started",
  pro: "Get Pro",
  power: "Get Power",
  max: "Get Max",
};

/** Rows of the comparison table that are the same on every plan. */
const INCLUDED_EVERYWHERE = [
  "Chat on the web and iPhone",
  "Text okthx by iMessage, SMS or WhatsApp",
  "Use okthx from Slack and Teams",
  "Gmail, Google Calendar and Outlook",
  "Email triage, drafts and replies",
  "Scheduling and meeting invitations",
  "Reminders and routines",
  "Skills and MCP connectors",
  "Memory and preferences",
];

/**
 * The public pricing page. Reachable only by URL for now: nothing in the app
 * links here, and it mounts before the signed-in app so a visitor with no
 * session never fires an authenticated request.
 */
export const Pricing = () => {
  const { data, isPending, isError } = useQuery(pricingCatalogQuery);
  const [interval, setInterval] = useState<BillingInterval>("year");
  const saving = data ? bestAnnualSaving(data.catalog) : null;

  return (
    <div className="min-h-dvh bg-background overflow-y-auto">
      <header className="mx-auto flex max-w-6xl items-center justify-between px-4 py-5 sm:px-6">
        <Link href="/" aria-label="okthx home">
          <OkThxWordmark className="h-[24px] w-[102px]" />
        </Link>
        <Button asChild size="sm" variant="outline" data-statsig-name="pricing-open-app">
          <Link href="/">Open okthx</Link>
        </Button>
      </header>

      <main className="mx-auto max-w-6xl px-4 pb-24 sm:px-6">
        <section className="flex flex-col items-center gap-4 pt-10 pb-10 text-center sm:pt-16">
          <h1 className="font-serif text-4xl italic sm:text-5xl">Plans that grow with you</h1>
          <p className="max-w-xl text-base text-muted-foreground">
            Every plan can do everything okthx does. Bigger plans let it take on more of your week.
          </p>
          <IntervalToggle value={interval} onChange={setInterval} saving={saving} />
        </section>

        {isError ? (
          <p className="py-16 text-center text-sm text-muted-foreground" role="alert">
            We couldn't load prices right now. Refresh the page to try again.
          </p>
        ) : (
          <section
            aria-label="Plans"
            className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4"
            data-testid="pricing-plans"
          >
            {isPending
              ? Array.from({ length: 4 }, (_, i) => (
                  <Skeleton key={i} className="h-[440px] rounded-2xl" />
                ))
              : data.catalog.map((entry) => (
                  <PlanCard key={entry.tier} entry={entry} interval={interval} />
                ))}
          </section>
        )}

        {data && <Comparison catalog={data.catalog} schedule={data} />}
        <Faq schedule={data ?? null} />

        <footer className="mt-16 space-y-1 text-center text-xs text-muted-foreground">
          <p>Prices are in US dollars. Checkout may show and charge them in your local currency.</p>
          <p>Usage limits apply to Free, Pro and Power. Max is effectively unlimited.</p>
        </footer>
      </main>
    </div>
  );
};

const INTERVAL_LABELS: Record<BillingInterval, string> = { month: "Monthly", year: "Annual" };

/** Monthly or Annual, for every card's price, with the best annual saving beside it. */
const IntervalToggle = ({
  value,
  onChange,
  saving,
}: {
  value: BillingInterval;
  onChange: (interval: BillingInterval) => void;
  saving: number | null;
}) => (
  <div className="flex flex-wrap items-center justify-center gap-3">
    <ButtonGroup aria-label="Billing interval">
      {BILLING_INTERVALS.map((interval) => (
        <Button
          key={interval}
          size="sm"
          variant={interval === value ? "default" : "outline"}
          aria-pressed={interval === value}
          onClick={() => onChange(interval)}
          data-statsig-name={`pricing-interval-${interval}`}
        >
          {INTERVAL_LABELS[interval]}
        </Button>
      ))}
    </ButtonGroup>
    {saving !== null && (
      <span className="text-xs font-medium text-primary">Save up to {saving}% annually</span>
    )}
  </div>
);

/**
 * The best whole-percent saving annual billing gives over twelve monthly
 * payments, across the plans sold both ways, or null when none is.
 */
export function bestAnnualSaving(catalog: CatalogEntryResp[]): number | null {
  let best: number | null = null;
  for (const { prices } of catalog) {
    if (!prices.month || !prices.year || prices.month.currency !== prices.year.currency) continue;
    const saving = Math.round((1 - prices.year.unitAmount / (prices.month.unitAmount * 12)) * 100);
    if (saving > 0 && (best === null || saving > best)) best = saving;
  }
  return best;
}

type PriceDisplay = { amount: string; per: string | null; note: string };

/**
 * How a card states its price. Annual prices are shown per month, the way
 * people compare plans, with the yearly total underneath. A plan with no
 * annual price falls back to its monthly one and says so.
 */
export function priceDisplay(
  entry: CatalogEntryResp,
  interval: BillingInterval,
): PriceDisplay | null {
  if (entry.tier === "free") {
    return { amount: FREE_PRICE_LABEL, per: null, note: "Free for everyone" };
  }
  const { month, year } = entry.prices;
  if (interval === "year" && year) {
    const perMonth: PriceResp = { ...year, unitAmount: Math.round(year.unitAmount / 12) };
    return {
      amount: formatPrice(perMonth),
      per: "/ month",
      note: `${formatPrice(year)} billed ${intervalAdverb("year")}`,
    };
  }
  if (month) {
    return {
      amount: formatPrice(month),
      per: "/ month",
      note: interval === "year" ? "Billed monthly only" : `Billed ${intervalAdverb("month")}`,
    };
  }
  return null;
}

/** One plan: its name, size, price on the chosen interval, a way to get it, and what it adds. */
const PlanCard = ({ entry, interval }: { entry: CatalogEntryResp; interval: BillingInterval }) => {
  const price = priceDisplay(entry, interval);
  const { lead, bullets } = CARD_BULLETS[entry.tier];
  const ctaHref = entry.tier === "free" ? "/" : BILLING_PATH;

  return (
    <article
      className="flex flex-col gap-5 rounded-2xl border bg-card p-6"
      data-testid={`pricing-plan-${entry.tier}`}
      aria-labelledby={`pricing-plan-${entry.tier}-name`}
    >
      <div className="space-y-1">
        <div className="flex items-center gap-2">
          <h2 id={`pricing-plan-${entry.tier}-name`} className="text-xl font-semibold">
            {PLAN_TIER_LABELS[entry.tier]}
          </h2>
          {entry.multiple !== null && <Badge variant="secondary">{entry.multiple}x usage</Badge>}
        </div>
        {/* Two lines tall on wide screens, so prices line up across the row. */}
        <p className="text-sm text-muted-foreground sm:min-h-10">{TIER_TAGLINES[entry.tier]}</p>
      </div>

      <div className="min-h-[68px]">
        {price ? (
          <>
            <div className="flex items-baseline gap-1">
              <span className="text-4xl font-semibold tracking-tight">{price.amount}</span>
              {price.per && <span className="text-sm text-muted-foreground">{price.per}</span>}
            </div>
            <p className="text-xs text-muted-foreground">{price.note}</p>
          </>
        ) : (
          <p className="text-sm text-muted-foreground">Not available right now</p>
        )}
      </div>

      {price ? (
        <Button
          asChild
          variant={entry.tier === "free" ? "outline" : "primary"}
          className="w-full"
          data-statsig-name={`pricing-cta-${entry.tier}`}
        >
          <Link href={ctaHref}>{CTA_LABELS[entry.tier]}</Link>
        </Button>
      ) : (
        <Button
          variant="outline"
          className="w-full"
          disabled
          data-statsig-name={`pricing-cta-${entry.tier}-unavailable`}
        >
          {CTA_LABELS[entry.tier]}
        </Button>
      )}

      <div className="space-y-2 text-sm">
        {lead && <p className="font-medium">{lead}</p>}
        <ul className="space-y-2">
          {bullets.map((bullet) => (
            <li key={bullet} className="flex gap-2">
              <Check className="mt-0.5 size-4 shrink-0 text-primary" aria-hidden />
              <span>{bullet}</span>
            </li>
          ))}
        </ul>
      </div>
    </article>
  );
};

/** When allowances reset under the policy in force now, as the server reports it. */
type ResetSchedule = Pick<PublicCatalogResp, "allowancePeriod" | "resetsAt">;

/**
 * The comparison table's reset cell. A weekly policy resets on a fixed rule;
 * any other window is named by its actual next reset, since a window cut short
 * by a policy change ends early rather than on a calendar boundary.
 */
function resetCell({ allowancePeriod, resetsAt }: ResetSchedule): string {
  return allowancePeriod === "week"
    ? "Every Monday, 00:00 UTC"
    : `Next: ${formatResetMoment(resetsAt)}`;
}

/**
 * The FAQ's answer to when usage resets. Only the weekly rule needs the
 * Sunday-evening note: Monday 00:00 UTC is still Sunday across the Americas.
 */
function resetAnswer(schedule: ResetSchedule | null): string {
  if (!schedule) return "Settings → Billing shows when your allowance next resets.";
  if (schedule.allowancePeriod === "week") {
    return "Every Monday at 00:00 UTC, whatever day you subscribed. In the Americas that's Sunday evening local time.";
  }
  return `Your current allowance resets on ${formatResetMoment(schedule.resetsAt)}, whatever day you subscribed.`;
}

function usageCell(entry: CatalogEntryResp): string {
  if (entry.multiple === null) return "A small allowance";
  return entry.marketedUnlimited
    ? `${entry.multiple}x Pro · effectively unlimited`
    : `${entry.multiple}x Pro`;
}

/** Every plan side by side: usage, when it resets, what's included, and how it bills. */
const Comparison = ({
  catalog,
  schedule,
}: {
  catalog: CatalogEntryResp[];
  schedule: ResetSchedule;
}) => {
  const paid = (entry: CatalogEntryResp) => entry.tier !== "free";
  // A cell is text, or whether the plan includes the row.
  const rows: Array<{ label: string; cells: Array<string | boolean> }> = [
    { label: "Usage", cells: catalog.map(usageCell) },
    { label: "Usage resets", cells: catalog.map(() => resetCell(schedule)) },
    ...INCLUDED_EVERYWHERE.map((label) => ({ label, cells: catalog.map(() => true) })),
    { label: "Monthly billing", cells: catalog.map((e) => paid(e) && e.prices.month !== null) },
    { label: "Annual billing", cells: catalog.map((e) => paid(e) && e.prices.year !== null) },
    { label: "Cancel any time", cells: catalog.map(paid) },
  ];

  return (
    <section className="mt-20 space-y-4" aria-labelledby="pricing-compare">
      <h2 id="pricing-compare" className="text-center font-serif text-3xl italic">
        Compare plans
      </h2>
      {/* Positioned so the cells' absolutely placed sr-only labels stay inside the scroller. */}
      <div className="relative overflow-x-auto rounded-2xl border bg-card">
        <table className="w-full min-w-[640px] text-sm">
          <thead>
            <tr className="border-b">
              <th scope="col" className="p-4 text-left font-medium text-muted-foreground">
                <span className="sr-only">Feature</span>
              </th>
              {catalog.map((entry) => (
                <th key={entry.tier} scope="col" className="p-4 text-left font-semibold">
                  {PLAN_TIER_LABELS[entry.tier]}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.label} className="border-b last:border-b-0">
                <th scope="row" className="p-4 text-left font-normal text-muted-foreground">
                  {row.label}
                </th>
                {row.cells.map((cell, i) => (
                  <td key={catalog[i]?.tier ?? i} className="p-4">
                    {typeof cell === "string" ? cell : <Included value={cell} />}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
};

/** A check or a dash, read out as words. */
const Included = ({ value }: { value: boolean }) =>
  value ? (
    <>
      <Check className="size-4 text-primary" aria-hidden />
      <span className="sr-only">Included</span>
    </>
  ) : (
    <>
      <Minus className="size-4 text-muted-foreground" aria-hidden />
      <span className="sr-only">Not included</span>
    </>
  );

type FaqItem = { question: string; answer: string };

function faqGroups(
  schedule: ResetSchedule | null,
): Array<{ id: string; label: string; items: FaqItem[] }> {
  const window = schedule?.allowancePeriod ?? "period";
  return [
    {
      id: "plans",
      label: "Plans and usage",
      items: [
        {
          question: "What's the difference between the plans?",
          answer:
            "Only how much okthx can take on. Every plan includes every feature. Pro is the baseline, Power gives you 5x Pro's usage, and Max gives you 20x, which is effectively unlimited. Free is a small allowance for trying okthx out.",
        },
        {
          question: "How does usage work?",
          answer: `Everything okthx does for you uses some of your allowance, and bigger jobs use more than small ones. Settings → Billing shows how much of this ${window}'s allowance you've used, as a percentage.`,
        },
        {
          question: "When does my usage reset?",
          answer: resetAnswer(schedule),
        },
        {
          question: "What happens if I run out?",
          answer:
            "okthx stops taking on new work until your allowance resets, and tells you when that is. You can upgrade at any point to keep going, and anything you've already asked for picks up where it left off. Nothing you've written is lost.",
        },
        {
          question: "Is Max really unlimited?",
          answer:
            "Effectively, yes. Max is made for people who run most of their day through okthx.",
        },
      ],
    },
    {
      id: "billing",
      label: "Billing and payments",
      items: [
        {
          question: "Can I pay annually?",
          answer:
            "Yes. Annual billing costs less than twelve monthly payments. Pick Annual above to see the per-month price.",
        },
        {
          question: "How do I pay?",
          answer:
            "By card, through Stripe. Checkout may show the price in your local currency and charge you at that day's exchange rate.",
        },
        {
          question: "When am I charged for an upgrade?",
          answer:
            "Right away. On the same billing interval you pay the difference for the rest of the current period. Moving from monthly to annual starts a new year today, less credit for the unused part of your current plan. If the payment fails, your plan doesn't change.",
        },
      ],
    },
    {
      id: "manage",
      label: "Managing your plan",
      items: [
        {
          question: "How do I upgrade?",
          answer: "Go to Settings → Billing and choose a plan. Upgrades apply immediately.",
        },
        {
          question: "What happens when I downgrade?",
          answer:
            "You keep your current plan until the end of the period you've paid for, then move to the smaller one. You can change your mind and keep your plan any time before then.",
        },
        {
          question: "How do I cancel?",
          answer:
            "In Settings → Billing, choose Cancel plan or Switch to Free. You keep your plan until the end of the period you've paid for, and you can resume it any time before then.",
        },
        {
          question: "What happens to my data if I go back to Free?",
          answer:
            "Nothing. Your data, connectors and routines all stay. Only the allowance changes.",
        },
      ],
    },
  ];
}

/** The FAQ, grouped into tabs. */
const Faq = ({ schedule }: { schedule: ResetSchedule | null }) => {
  const groups = faqGroups(schedule);
  return (
    <section className="mx-auto mt-20 max-w-3xl space-y-6" aria-labelledby="pricing-faq">
      <h2 id="pricing-faq" className="text-center font-serif text-3xl italic">
        Frequently asked questions
      </h2>
      <Tabs defaultValue={groups[0]?.id}>
        <div className="overflow-x-auto">
          <TabsList variant="underline" className="w-full justify-start sm:justify-center">
            {groups.map((group) => (
              <TabsTrigger key={group.id} value={group.id} variant="underline" size="underline">
                {group.label}
              </TabsTrigger>
            ))}
          </TabsList>
        </div>
        {groups.map((group) => (
          <TabsContent key={group.id} value={group.id} className="divide-y">
            {group.items.map((item) => (
              <FaqEntry key={item.question} item={item} />
            ))}
          </TabsContent>
        ))}
      </Tabs>
    </section>
  );
};

/** One question that opens to show its answer. */
const FaqEntry = ({ item }: { item: FaqItem }) => {
  const [open, setOpen] = useState(false);
  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <CollapsibleTrigger className="flex w-full cursor-pointer items-center justify-between gap-4 py-4 text-left font-medium">
        {item.question}
        <ChevronDown
          className={cn("size-4 shrink-0 transition-transform", open && "rotate-180")}
          aria-hidden
        />
      </CollapsibleTrigger>
      <CollapsibleContent className="pb-4 text-sm text-muted-foreground">
        {item.answer}
      </CollapsibleContent>
    </Collapsible>
  );
};
